import { useEffect, useRef } from 'react';

import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';
import type { BylineConnectionIcon } from '../utils/getBylineConnectionIcons.ts';
import { getBylineIconCountAttributes } from '../utils/getBylineIconCountAttributes.ts';

import { getBylineIconCountSettledEvent } from './bylineIconCountAnalytics.ts';
import type { UseIncomingLinksResult } from './useIncomingLinks.ts';
import type { UseRelatedContentResult } from './useRelatedContent.ts';
import type { UseSmartLinksResult } from './useSmartLinks.ts';

export type BylineIconCountAnalyticsOptions = {
	contentId: string;
	visibility: {
		hiddenBySsr: boolean;
		reservedIconCount: number;
		isSsrLoad: boolean;
		devDocsIcons: readonly BylineConnectionIcon[] | undefined;
	};
	data: {
		outgoing: Pick<UseSmartLinksResult, 'links' | 'loading' | 'refreshing'>;
		incoming: Pick<UseIncomingLinksResult, 'twgLinks' | 'settled'>;
		relatedContent: Pick<UseRelatedContentResult, 'links' | 'loading'>;
	};
};

/**
 * Fires `bylineIconCount settled` once per content id, after every source has finished loading
 * rather than when the stack first stops changing, so the counts are final. A page SSR hid stays
 * silent: nothing was painted to compare against.
 */
export const useBylineIconCountAnalytics = ({
	contentId,
	visibility,
	data,
}: BylineIconCountAnalyticsOptions): void => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const firedForContentIdRef = useRef<string | null>(null);
	const { hiddenBySsr, reservedIconCount, isSsrLoad, devDocsIcons } = visibility;
	const { outgoing, incoming, relatedContent } = data;
	const settled =
		!outgoing.loading && !outgoing.refreshing && incoming.settled && !relatedContent.loading;

	useEffect(() => {
		if (!settled || hiddenBySsr || firedForContentIdRef.current === contentId) {
			return;
		}
		firedForContentIdRef.current = contentId;
		createAnalyticsEvent(
			getBylineIconCountSettledEvent(
				getBylineIconCountAttributes({
					sources: {
						additionalIcons: devDocsIcons,
						relatedContentLinks: relatedContent.links,
						incomingLinks: incoming.twgLinks,
						outgoingLinks: outgoing.links,
					},
					ssrPlaceholderCount: reservedIconCount,
					isSsrLoad,
				}),
			),
		).fire();
	}, [
		settled,
		hiddenBySsr,
		contentId,
		createAnalyticsEvent,
		devDocsIcons,
		relatedContent.links,
		incoming.twgLinks,
		outgoing.links,
		reservedIconCount,
		isSsrLoad,
	]);
};
